import React from 'react';
import styled from 'styled-components';
import { PrimaryButton, TertiaryButton } from 'styleguide/Buttons';
import { colors } from 'styleguide/colors';
import { DividerLine } from 'styleguide/DividerLine';
import { LabelledFormInput } from 'styleguide/Inputs';
import { H1, P2, P3 } from 'styleguide/Texts';
import { View } from 'styleguide/View';
import { LoginWithGoogleButton, SocialLoginButton } from '../../../components/SocialLoginButton';
import { FlexRow } from 'styleguide/Row';
import { t } from 'translations/t';

const Spacer = styled.div`
  margin-top: 92px;
  @media (max-width: 1024px) {
    margin-top: 16px;
  }
`;

interface LoginCardProps {
  email: string;
  isEmailLocked?: boolean;
  onUpdateEmail: (value: string) => void;
  emailErrorStatus?: string;
  password: string;
  onUpdatePassword: (value: string) => void;
  passwordErrorStatus?: string;
  isSignupEnabled: boolean;
  isLoading: boolean;
  onSignup: () => void;
  signupErrorMessage?: string;
  onGoogleSignup: () => void;
  onAppleSignup: () => void;
  onExistingUserPress: () => void;
}

export const SignupCard = ({
  email,
  isEmailLocked,
  onUpdateEmail,
  emailErrorStatus,
  password,
  onUpdatePassword,
  passwordErrorStatus,
  isSignupEnabled,
  isLoading,
  onSignup,
  signupErrorMessage,
  onGoogleSignup,
  onAppleSignup,
  onExistingUserPress,
}: LoginCardProps) => {
  return (
    <View style={{ width: '100%', maxWidth: 400 }}>
      <View style={{ flex: 1 }}>
        <Spacer />
        <H1 style={{ textAlign: 'center', marginBottom: 16 }}>{t('web.signup.signUp')}</H1>
        <P2 style={{ textAlign: 'center', marginBottom: 48 }}>{t('web.signup.createAccount')}</P2>
        <LoginWithGoogleButton
          action="signup"
          style={{ marginBottom: 16, justifyContent: 'center' }}
          onClick={onGoogleSignup}
        />
        <SocialLoginButton
          action="signup"
          style={{ marginBottom: 16 }}
          type="apple"
          onClick={onAppleSignup}
        />
        <FlexRow style={{ marginTop: 16, marginBottom: 20 }}>
          <DividerLine />
          <P3 type="secondary" style={{ width: '100%', textAlign: 'center' }}>
            {t('web.login.loginCard.orWithEmail')}
          </P3>
          <DividerLine />
        </FlexRow>
        <form onSubmit={e => e.preventDefault()}>
          <LabelledFormInput
            label={t('web.signup.signupCard.formInput.email')}
            spellCheck={false}
            containerStyle={{ marginBottom: '16px' }}
            value={email}
            autoCapitalize="none"
            disabled={isEmailLocked ?? false}
            onChange={event => onUpdateEmail(event.currentTarget.value)}
            errorMessage={emailErrorStatus}
          />
          <LabelledFormInput
            label={t('web.login.loginCard.formInput.password')}
            type={'password'}
            containerStyle={{ marginBottom: '16px' }}
            value={password}
            onChange={event => onUpdatePassword(event.currentTarget.value)}
            errorMessage={passwordErrorStatus}
          />
          <PrimaryButton
            type={'submit'}
            title={t('web.signup.signUp')}
            size="lg"
            style={{ width: '100%', marginBottom: '24px' }}
            enabled={isSignupEnabled}
            loading={isLoading}
            onClick={onSignup}
          />
          <P3>
            {t('web.signup.signUpCard.termsConditionsPrivacy', {
              termsConditions: (
                <a
                  href="https://www.hevyapp.com/app-terms-conditions/"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('web.signup.signUpCard.termsConditions')}
                </a>
              ),
              privacyPolicy: (
                <a href="https://www.hevyapp.com/privacy-policy/" target="_blank" rel="noreferrer">
                  {t('web.signup.signUpCard.privacyPolicy')}
                </a>
              ),
            })}
          </P3>
        </form>
        {!!signupErrorMessage && (
          <View>
            <P2 style={{ color: colors.red, marginBottom: '16px' }}>{signupErrorMessage}</P2>
          </View>
        )}
        <TertiaryButton
          onClick={onExistingUserPress}
          style={{ width: '100%', marginBottom: '0px', gap: 4 }}
        >
          <span style={{ color: colors.black }}>{t('web.signup.signUpCard.alreadyHaveAcc')}</span>
          <span style={{ textDecoration: 'underline' }}>
            {t('web.login.loginCard.formInput.loginBtn')}
          </span>
        </TertiaryButton>
      </View>
    </View>
  );
};
